<template>
  <div class="payment-link-step-methods">
    <div class="step-description">
      <p>{{ t('Selecione quais formas de pagamento o cliente poderá usar') }}</p>
    </div>

    <ButtonListMultiple
      data-testid="payment-links-step-methods-input"
      v-model="selectedMethods"
      :itensSelect="paymentMethodOptions"
      :validation="() => isValid"
      :ngRequired="true"
      :hideLabel="true"
      :hideSearch="true"
      elemId="payment_link_methods">
    </ButtonListMultiple>

    <div class="methods-info" v-if="selectedMethods.length > 0">
      <i class="pi pi-info-circle"></i>
      <span v-if="selectedMethods.length === 3">
        {{ t('O cliente poderá escolher entre PIX, Boleto ou Cartão') }}
      </span>
      <span v-else-if="selectedMethods.length === 1 && selectedMethods[0] === 'credit_card'">
        {{ t('Apenas pagamento via Cartão de Crédito (permite parcelamento)') }}
      </span>
      <span v-else>
        {{ t('Formas selecionadas') }}: {{ getSelectedMethodsLabel() }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface PaymentMethodOption {
  ID: string;
  Name: string;
}

interface PaymentLinkFormData {
  payment_methods?: string[];
  [key: string]: unknown;
}

const { t } = useI18n();

// Payment method options
const paymentMethodOptions: PaymentMethodOption[] = [
  { ID: 'pix', Name: 'PIX' },
  { ID: 'boleto', Name: 'Boleto' },
  { ID: 'credit_card', Name: t('Cartão de Crédito') }
];

const props = defineProps<{
  modelValue: PaymentLinkFormData;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: PaymentLinkFormData): void;
}>();

// Selected methods with 2-way binding
const selectedMethods = computed<string[]>({
  get: () => props.modelValue.payment_methods || [],
  set: (value: string[]) => {
    emit('update:modelValue', {
      ...props.modelValue,
      payment_methods: value
    });
  }
});

// Validation
const isValid = computed<boolean>(() => {
  return selectedMethods.value.length > 0;
});

// Methods
const getSelectedMethodsLabel = (): string => {
  return selectedMethods.value.map(m => {
    const method = paymentMethodOptions.find(pm => pm.ID === m);
    return method ? method.Name : m;
  }).join(', ');
};

// Expose validation for wizard
defineExpose({
  isValid
});
</script>

<style scoped>
.payment-link-step-methods {
  padding: 1rem 0;
}

.step-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
}

.step-description p {
  margin: 0;
}

.methods-info {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: var(--blue-50);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--blue-700);
  font-size: 0.875rem;
}

.methods-info i {
  font-size: 1.25rem;
  flex-shrink: 0;
}
</style>
